// Transporte Web Bluetooth (BLE) para impresión térmica ESC/POS.
//
// Servicio SIN React, portado de la lógica YA PROBADA contra hardware real del spike
// (plick-print-web-spike: flow.js / spike.js). Reúne: descubrimiento de característica
// escribible, escritura en chunks, reconexión silenciosa (getDevices) y reconexión
// asegurada antes de cada impresión (ensureReady) porque las térmicas BLE se duermen.
//
// REGLA CRÍTICA: nunca se accede a `navigator.bluetooth` en el top-level de este módulo
// (rompería los tests en jsdom, que no lo define). Todo acceso vive dentro de funciones,
// detrás de la guarda `isWebBleSupported()`.

import type {
  Bluetooth,
  BluetoothDevice,
  BluetoothRemoteGATTCharacteristic,
  BluetoothRemoteGATTServer,
} from '../../../types/web-bluetooth';

// UUIDs de servicios BLE de las térmicas conocidas (spike.js / flow.js). Se usan como
// `optionalServices` en requestDevice y para descubrir servicios con getPrimaryServices.
export const KNOWN_SERVICES: string[] = [
  '000018f0-0000-1000-8000-00805f9b34fb', // Goojprt / Zjiang / Munbyn / POS-58 (más común)
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // MTP-2/3 (ISSC / Microchip)
  '0000ae30-0000-1000-8000-00805f9b34fb', // CAT / mini
  '0000ff00-0000-1000-8000-00805f9b34fb', // PeriPage / FoMemo
  '0000ffe0-0000-1000-8000-00805f9b34fb', // BLE genéricos (HM-10 / JDY)
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Nordic-UART-like
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART Service
];

// Tamaño de chunk de escritura (bytes) y respiro entre chunks (ms). El spike usaba 512/20, pero
// en hardware (PT-220) un payload grande se TRUNCABA tras el primer chunk: escribir 512 B por
// `writeValueWithoutResponse` (sin ACK) más rápido de lo que drena el enlace → se pierden los
// chunks siguientes. Bajamos el default a un tamaño que cabe en el MTU típico BLE (~185) y damos
// más respiro. Configurables sin redeploy vía localStorage (panel debug) para tunear por modelo.
const DEFAULT_CHUNK_SIZE = 180;
const DEFAULT_CHUNK_DELAY_MS = 30;

// Escalera de velocidad (slider del usuario). Cada paso es un par (chunk, respiro) ordenado de
// MÁS COMPATIBLE/lento a MÁS RÁPIDO/arriesgado, en progresión MONÓTONA de throughput. Cubre TODO
// el espectro de hardware, no solo el de la impresora de prueba:
//  - Pasos 0–1: MÁS compatibles que el default (chunk chico + respiro grande), para térmicas MUY
//    básicas que manchan incluso en el seguro. Es el escape hacia abajo.
//  - Paso 2: el probado-seguro (180/30) → DEFAULT. Funciona en la térmica de bolsillo típica.
//  - Pasos 3–6: chunk fijo en 180 y se DRENA el respiro 30→0. Palanca segura: en ruta con-ACK el
//    respiro es redundante (el ACK regula) → acelera sin arriesgar. En sin-ACK, bajar de más mancha.
//  - Pasos 7–10: con el respiro ya en 0, se EMPUJA el chunk 220→512. El rango para impresoras
//    POTENTES (80mm buenas, de red) que tragan payloads grandes. Aquí aparecen manchas/cortes si la
//    térmica no da el ancho de banda → el usuario retrocede un paso.
// Cada impresora explota su PROPIO rango: sube hasta justo antes de mancharse, sin que adivinemos
// su capacidad. DEFAULT_SPEED_STEP marca el probado-seguro (no el extremo inferior).
export const WEB_BLE_SPEED_LADDER: ReadonlyArray<{ chunk: number; delay: number }> = [
  { chunk: 100, delay: 50 }, // 0 — ultra-compatible (térmicas muy básicas)
  { chunk: 140, delay: 40 }, // 1 — básicas
  { chunk: 180, delay: 30 }, // 2 — Segura (DEFAULT, probado en hardware)
  { chunk: 180, delay: 20 }, // 3
  { chunk: 180, delay: 10 }, // 4
  { chunk: 180, delay: 5 }, // 5
  { chunk: 180, delay: 0 }, // 6 — respiro drenado (fin de la palanca segura)
  { chunk: 220, delay: 0 }, // 7 — empieza a subir el chunk (riesgo de manchas)
  { chunk: 280, delay: 0 }, // 8
  { chunk: 384, delay: 0 }, // 9 — potentes (80mm buenas)
  { chunk: 512, delay: 0 }, // 10 — muy potentes / red
];
// El default NO es el extremo inferior: es el punto probado-seguro (180/30), con margen hacia abajo
// (más compatible) y hacia arriba (más rápido). resolveSpeedStep(undefined) cae aquí.
export const DEFAULT_SPEED_STEP = 2;
export const SPEED_STEP_MAX = WEB_BLE_SPEED_LADDER.length - 1;

// Traduce el paso del slider (0..SPEED_STEP_MAX) al par de transporte (chunk/respiro), con clamp.
// `undefined`/no-finito → DEFAULT_SPEED_STEP (el probado-seguro); fuera de rango → clamp al extremo.
// Es la fuente de verdad del slider del usuario.
export const resolveSpeedStep = (step: number | undefined): { chunkSize: number; chunkDelay: number } => {
  const i =
    typeof step === 'number' && Number.isFinite(step)
      ? Math.max(0, Math.min(SPEED_STEP_MAX, Math.round(step)))
      : DEFAULT_SPEED_STEP;
  const { chunk, delay } = WEB_BLE_SPEED_LADDER[i];
  return { chunkSize: chunk, chunkDelay: delay };
};

// Tamaño de chunk efectivo. Precedencia: override de DEBUG (localStorage, para calibrar en
// hardware) > valor por-impresora (del slider) > default. Así las perillas crudas del panel
// debug siempre ganan durante la calibración, sin pelear con el slider del usuario.
const getChunkSize = (perPrinter?: number): number => {
  try {
    const v = parseInt(typeof window !== 'undefined' ? window.localStorage?.getItem('webBleChunkSize') || '' : '', 10);
    if (Number.isFinite(v) && v >= 20 && v <= 2048) return v;
  } catch {
    /* sigue a per-printer / default */
  }
  if (typeof perPrinter === 'number' && perPrinter >= 20 && perPrinter <= 2048) return perPrinter;
  return DEFAULT_CHUNK_SIZE;
};

// Respiro entre chunks efectivo. Misma precedencia: override de debug > por-impresora > default.
const getChunkDelay = (perPrinter?: number): number => {
  try {
    const v = parseInt(typeof window !== 'undefined' ? window.localStorage?.getItem('webBleChunkDelay') || '' : '', 10);
    if (Number.isFinite(v) && v >= 0 && v <= 500) return v;
  } catch {
    /* sigue a per-printer / default */
  }
  if (typeof perPrinter === 'number' && perPrinter >= 0 && perPrinter <= 500) return perPrinter;
  return DEFAULT_CHUNK_DELAY_MS;
};

// Diagnóstico de tiempo de la ÚLTIMA transferencia (panel debug). Mide el envío real al
// hardware para cuantificar la lentitud reportada y aislar la causa: `bytes`/`chunks` (tamaño
// del ticket), `ms` (tiempo total de pared del writeChunked), `path` (ACK = writeValue con
// respuesta; el ACK ya provee flow-control, así que el `delay` es redundante en esa ruta; o
// noResp = writeValueWithoutResponse, donde el `delay` SÍ es la única regulación). Read-only:
// no cambia ningún byte ni el comportamiento de impresión.
export type WriteTransferStats = {
  bytes: number;
  chunks: number;
  ms: number;
  chunkSize: number;
  delay: number;
  path: 'ACK' | 'noResp';
};
let lastTransfer: WriteTransferStats | null = null;
export const lastTransferStats = (): WriteTransferStats | null => lastTransfer;

// Conexión activa de un dispositivo físico: el device + su característica escribible.
export type BleConnection = {
  device: BluetoothDevice;
  writeChar: BluetoothRemoteGATTCharacteristic;
};

// Mapa de runtime deviceId -> conexión activa. Permite que varios perfiles lógicos
// (Caja, Cocina…) que apuntan al MISMO dispositivo físico compartan una sola conexión,
// y que ensureReady reutilice/reconecte sin re-emparejar. Vive en memoria del módulo;
// se reconstruye al recargar la página (los deviceId son volátiles entre sesiones).
const runtime = new Map<string, BleConnection>();

// Handles de dispositivos YA emparejados en esta sesión (deviceId -> BluetoothDevice). A
// diferencia de `runtime`, NO se borra al desconectarse: conservamos el handle para poder
// reconectar DIRECTO con `gatt.connect()` (sin `getDevices()` ni gesto del usuario) cuando
// la térmica se duerme entre impresiones. Solo se pierde al recargar la página.
const pairedDevices = new Map<string, BluetoothDevice>();

// Dispositivos a los que ya se les enganchó el listener `gattserverdisconnected`.
// Web Bluetooth reutiliza la MISMA instancia de BluetoothDevice entre reconexiones
// (getDevices / ensureReady), así que sin esta guarda los listeners se acumularían en
// cada attachDevice → fuga de memoria y N invocaciones por desconexión. WeakSet para no
// retener el device si el navegador lo descarta.
const disconnectListenerAttached = new WeakSet<BluetoothDevice>();

// Dispositivos a los que ya se les enganchó el listener `advertisementreceived` vía
// watchAndReconnect. El keep-alive (useWebBleKeepAlive) lo invoca en CADA reconexión, y
// como las térmicas BLE se duermen entre pedidos el ciclo despierta→reconecta→duerme es
// NORMAL: sin esta guarda se acumularían N listeners por device (cada anuncio dispararía N
// attachDevice en paralelo) → fuga. WeakSet para no retener el device si el navegador lo
// descarta. El unsubscriber retornado quita el device del set (permite re-observar tras un
// teardown explícito). `let` porque el WeakSet no tiene .clear(): se reasigna en el reset.
let advertisementWatchAttached = new WeakSet<BluetoothDevice>();

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Guarda de soporte. NO accede en top-level: solo dentro de funciones. jsdom no define
// navigator.bluetooth, por lo que en tests devuelve false (camino seguro).
export const isWebBleSupported = (): boolean => typeof navigator !== 'undefined' && Boolean(navigator.bluetooth);

const getBluetooth = (): Bluetooth => {
  if (typeof navigator === 'undefined' || !navigator.bluetooth) {
    throw new Error('Web Bluetooth no disponible en este entorno.');
  }
  return navigator.bluetooth;
};

// Abre el chooser del navegador (requiere gesto de usuario). Si se pasa namePrefix,
// filtra para que solo aparezca la impresora del usuario; si no, acepta todos los
// dispositivos (flujo de primer emparejamiento del spike: addPrinter).
export const requestPrinter = async ({ namePrefix }: { namePrefix?: string } = {}): Promise<BluetoothDevice> => {
  const bluetooth = getBluetooth();
  if (namePrefix && namePrefix.trim()) {
    return bluetooth.requestDevice({
      filters: [{ namePrefix: namePrefix.trim() }],
      optionalServices: KNOWN_SERVICES,
    });
  }
  return bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: KNOWN_SERVICES,
  });
};

// Descubre la primera característica escribible (write o writeWithoutResponse) recorriendo
// los servicios primarios del servidor GATT (spike flow.js: findWritableChar).
const findWritableChar = async (
  server: BluetoothRemoteGATTServer,
): Promise<BluetoothRemoteGATTCharacteristic | null> => {
  for (const service of await server.getPrimaryServices()) {
    for (const characteristic of await service.getCharacteristics()) {
      if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
        return characteristic;
      }
    }
  }
  return null;
};

// Conecta el GATT con reintentos (3 intentos, backoff 0/250/500ms). Las térmicas BLE que
// acaban de despertar rechazan el primer connect; reintentar sube mucho la tasa de éxito.
const connectWithRetry = async (gatt: BluetoothRemoteGATTServer): Promise<BluetoothRemoteGATTServer> => {
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await sleep(attempt * 250);
    try {
      return await gatt.connect();
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('No se pudo conectar el GATT.');
};

// Lock por dispositivo de las operaciones de attach EN VUELO. `useWebBleKeepAlive` puede disparar
// attachDevice desde DOS lados a la vez (el intervalo de warm-up + el handler `advertisementreceived`
// de watchAndReconnect) → sin lock, dos `gatt.connect()` concurrentes sobre el MISMO device causan
// un race (NetworkError / estado inconsistente). Compartir la misma promesa serializa el connect
// (Seer HIGH). Se limpia al resolver/fallar.
const attachInFlight = new Map<string, Promise<BleConnection>>();

// Conecta un dispositivo físico, descubre su característica escribible y lo registra en
// el runtime por su deviceId (spike flow.js: attachDevice). Idempotente respecto al GATT:
// si ya está conectado, reutiliza el servidor. Serializado por dispositivo (attachInFlight).
export const attachDevice = async (device: BluetoothDevice): Promise<BleConnection> => {
  const gatt = device.gatt;
  if (!gatt) {
    throw new Error('El dispositivo no expone GATT.');
  }
  // Si ya hay un attach en vuelo para este device, comparte su promesa: NO abras otro connect.
  const pending = attachInFlight.get(device.id);
  if (pending) return pending;
  const run = (async (): Promise<BleConnection> => {
    // Conserva el handle para reconexiones directas (sobrevive a las desconexiones).
    pairedDevices.set(device.id, device);
    // Limpia la conexión del runtime cuando el dispositivo se desconecta (se dormirá entre
    // pedidos). ensureReady la reconstruirá en la próxima impresión. Se engancha UNA sola
    // vez por instancia de device (attachDevice se llama repetido entre reconexiones).
    if (!disconnectListenerAttached.has(device)) {
      device.addEventListener('gattserverdisconnected', () => {
        runtime.delete(device.id);
      });
      disconnectListenerAttached.add(device);
    }
    // Conexión con reintentos: las térmicas BLE recién despiertas suelen RECHAZAR el primer
    // gatt.connect() (NetworkError) y aceptar al 2º/3º intento. Sin reintento, "Confirmar"
    // fallaba con la impresora justo despertando. Backoff corto entre intentos.
    const server = gatt.connected ? gatt : await connectWithRetry(gatt);
    const writeChar = await findWritableChar(server);
    if (!writeChar) {
      throw new Error('Impresora conectada pero sin característica escribible.');
    }
    const connection: BleConnection = { device, writeChar };
    runtime.set(device.id, connection);
    return connection;
  })();
  attachInFlight.set(device.id, run);
  try {
    return await run;
  } finally {
    attachInFlight.delete(device.id);
  }
};

// Reconexión SIN diálogo usando navigator.bluetooth.getDevices() (dispositivos ya
// autorizados por el usuario). Devuelve null si getDevices no está disponible o si la
// impresora guardada no se encuentra (spike flow.js: rama getDevices de confirm).
export const reconnectKnown = async ({
  deviceId,
  deviceName,
}: {
  deviceId?: string;
  deviceName?: string;
}): Promise<BleConnection | null> => {
  if (!isWebBleSupported()) return null;
  // Reuso directo: el dispositivo ya está en runtime y conectado (otro perfil lo abrió).
  if (deviceId) {
    const existing = runtime.get(deviceId);
    if (existing && existing.device.gatt?.connected) return existing;
    // Tenemos el handle de esta sesión aunque esté desconectado → reconectar DIRECTO con
    // gatt.connect() (no requiere getDevices ni gesto). Cubre "se durmió tras emparejar".
    const held = pairedDevices.get(deviceId);
    if (held) {
      try {
        return await attachDevice(held);
      } catch {
        /* sigue al fallback de getDevices */
      }
    }
  }
  const bluetooth = getBluetooth();
  if (!bluetooth.getDevices) return null;
  let known: BluetoothDevice[];
  try {
    known = await bluetooth.getDevices();
  } catch {
    // getDevices puede fallar (flag desactivado / permisos). Degradar a null: el caller
    // hará fallback a requestPrinter (chooser filtrado) dentro de un gesto de usuario.
    return null;
  }
  const match =
    (deviceId ? known.find((d) => d.id === deviceId) : undefined) ??
    (deviceName ? known.find((d) => d.name === deviceName) : undefined);
  if (!match) return null;
  return attachDevice(match);
};

// Garantiza una característica escribible lista ANTES de cada impresión. Las térmicas BLE
// se duermen entre pedidos: si el GATT no está conectado, reconecta en silencio
// (spike flow.js: ensureReady). Lanza si el device no tiene GATT.
export const ensureReady = async (device: BluetoothDevice): Promise<BluetoothRemoteGATTCharacteristic> => {
  const existing = runtime.get(device.id);
  if (existing && existing.device.gatt?.connected && existing.writeChar) {
    return existing.writeChar;
  }
  const connection = await attachDevice(device);
  return connection.writeChar;
};

// Lock de serialización a NIVEL DE MÓDULO. Cada componente/card monta su propia instancia
// de useWebBlePrint (con su isPrinting local), así que el guard de UI no coordina entre
// ellos: dos impresiones a la MISMA característica intercalarían sus chunks → ticket
// corrupto. Encadenar aquí garantiza orden estricto sin descartar ninguna impresión.
let writeLock: Promise<void> = Promise.resolve();

// Timeout de UNA escritura BLE. Un writeValue/writeValueWithoutResponse normal tarda ms, pero si la
// impresora se cuelga (apagada a mitad de la impresión, sin señal, firmware trabado) su promesa
// puede NO rejectar nunca. Sin timeout, el `writeLock` —global, compartido por TODAS las impresoras—
// jamás se libera y bloquea la impresión de todas hasta recargar la página (Seer HIGH). El race
// fuerza un reject → run() falla → `writeLock.catch()` libera el lock → las demás siguen imprimiendo.
// 5s es enorme para un chunk (≤512B se escribe en ms), así que no dispara falsos positivos.
const CHUNK_WRITE_TIMEOUT_MS = 5000;
const writeWithTimeout = (op: Promise<void>, ms: number): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`BLE write timeout (${ms}ms)`)), ms);
    op.then(
      () => {
        clearTimeout(timer);
        resolve();
      },
      (e) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });

// Escribe los bytes en chunks de CHUNK_SIZE. Prefiere writeValueWithoutResponse cuando la
// característica solo soporta sin-respuesta (más rápido y único modo en muchas térmicas);
// si no, writeValue. Respira CHUNK_DELAY_MS entre chunks (spike flow.js: writeChunked).
// Serializada: una escritura espera a que termine la anterior (ver writeLock).
export const writeChunked = async (
  writeChar: BluetoothRemoteGATTCharacteristic,
  bytes: Uint8Array,
  // Transporte por-impresora (del slider de velocidad). Si se omite, cae a default; el override
  // de debug (localStorage) siempre tiene prioridad sobre ambos (ver getChunkSize/getChunkDelay).
  transport?: { chunkSize?: number; chunkDelay?: number },
): Promise<void> => {
  const chunkSize = getChunkSize(transport?.chunkSize);
  const chunkDelay = getChunkDelay(transport?.chunkDelay);
  const useNoResp =
    writeChar.properties.writeWithoutResponse &&
    !writeChar.properties.write &&
    typeof writeChar.writeValueWithoutResponse === 'function';
  const run = async (): Promise<void> => {
    // Marca de tiempo de pared del envío real (diagnóstico, no afecta los bytes). Date.now()
    // existe en el navegador real; en jsdom/SSR cae a 0 → ms=0 (inocuo para los tests).
    const startedAt = typeof Date !== 'undefined' && typeof Date.now === 'function' ? Date.now() : 0;
    let chunks = 0;
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
      const part = bytes.slice(offset, offset + chunkSize);
      // Cada write con timeout: una impresora colgada rejecta en vez de bloquear el lock global.
      const op = useNoResp ? writeChar.writeValueWithoutResponse(part) : writeChar.writeValue(part);
      await writeWithTimeout(op, CHUNK_WRITE_TIMEOUT_MS);
      chunks += 1;
      await sleep(chunkDelay);
    }
    const endedAt = typeof Date !== 'undefined' && typeof Date.now === 'function' ? Date.now() : 0;
    lastTransfer = {
      bytes: bytes.length,
      chunks,
      ms: Math.max(0, endedAt - startedAt),
      chunkSize,
      delay: chunkDelay,
      path: useNoResp ? 'noResp' : 'ACK',
    };
  };
  // Encadena tras la escritura previa (haya resuelto o fallado) y avanza el lock.
  const result = writeLock.then(run, run);
  writeLock = result.catch(() => undefined);
  return result;
};

// Reconexión proactiva sin gesto vía watchAdvertisements (cuando el navegador lo soporta).
// Degrada en silencio si no está disponible. Devuelve una función para dejar de escuchar.
// Útil para auto-print por socket: la impresora vuelve a anunciarse al despertar.
export const watchAndReconnect = (
  device: BluetoothDevice,
  onReconnected: (connection: BleConnection) => void,
): (() => void) => {
  if (typeof device.watchAdvertisements !== 'function') {
    return () => undefined;
  }
  // Idempotencia: si ya estamos observando este device, no re-registramos listener ni
  // re-llamamos watchAdvertisements (el keep-alive invoca esto en cada reconexión y el
  // device se reutiliza entre ellas → si no, se acumularían listeners). No-op silencioso.
  if (advertisementWatchAttached.has(device)) {
    return () => undefined;
  }
  const handler = (): void => {
    if (device.gatt?.connected) return;
    attachDevice(device)
      .then(onReconnected)
      .catch(() => undefined);
  };
  device.addEventListener('advertisementreceived', handler);
  advertisementWatchAttached.add(device);
  device.watchAdvertisements().catch(() => undefined);
  // El unsubscriber quita el device del set: tras un teardown explícito se puede volver a
  // observar. NO lo quitamos dentro de `handler` (las reconexiones del keep-alive no son
  // teardowns y deben seguir cubiertas por la misma observación).
  return () => {
    device.removeEventListener('advertisementreceived', handler);
    advertisementWatchAttached.delete(device);
  };
};

// Helpers de runtime para que el hook React maneje el ciclo de vida sin reimplementar el Map.
export const getConnection = (deviceId: string): BleConnection | undefined => runtime.get(deviceId);

// DEBUG: describe los modos de escritura de la característica del dispositivo. 'write(ACK)' =
// con respuesta (fiable, con control de flujo); 'writeNoResp' = sin respuesta (rápido pero puede
// truncar payloads grandes sin pacing). '—' si no hay conexión viva.
export const describeWriteChar = (deviceId: string): string => {
  const props = runtime.get(deviceId)?.writeChar?.properties;
  if (!props) return '—';
  const modes: string[] = [];
  if (props.write) modes.push('write(ACK)');
  if (props.writeWithoutResponse) modes.push('writeNoResp');
  return modes.length ? modes.join('+') : 'otro';
};

// DEBUG: config EFECTIVA de troceo del transporte (chunk + delay) para un paso de velocidad dado.
// Precedencia: override de debug (localStorage) > valor por-impresora (slider, vía `perStep`) >
// default. Sin `perStep` equivale al paso 0 (= default), preservando el comportamiento previo.
export const currentChunkConfig = (perStep?: number): { size: number; delay: number } => {
  const t = resolveSpeedStep(perStep);
  return { size: getChunkSize(t.chunkSize), delay: getChunkDelay(t.chunkDelay) };
};

export const isConnected = (deviceId: string): boolean => {
  const connection = runtime.get(deviceId);
  return Boolean(connection && connection.device.gatt?.connected && connection.writeChar);
};

export const disconnect = (deviceId: string): void => {
  const connection = runtime.get(deviceId);
  if (connection && connection.device.gatt?.connected) {
    connection.device.gatt.disconnect();
  }
  runtime.delete(deviceId);
};

// Solo para tests: limpia el Map de runtime (no toca hardware ni localStorage).
export const __resetRuntimeForTests = (): void => {
  runtime.clear();
  pairedDevices.clear();
  attachInFlight.clear();
  // WeakSet no expone .clear(): reasignamos uno nuevo para soltar las guardas de observación.
  advertisementWatchAttached = new WeakSet<BluetoothDevice>();
  writeLock = Promise.resolve();
};
